import { call, cancel, getContext, put, select, take } from 'redux-saga/effects';

import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';

import {
  CONFIRM_BUY_PACKAGE,
  RESET_BUY_PACKAGE,
  setBuyPackageLoading,
  setBuyPackageStatus,
  setBuyPackageMessage,
  setBuyPackageHeldPayment,
} from '../../actions/buyPackage';
import { loadPublishTerms, EAccountType } from '../../redux/reducers';
import { buyPackage as buyPackageRequest } from '../../services/servicePackages';
import { selectBuyPackageModalData } from '../../selectors/buyPackage';
import { selectBillingUserType } from '../../selectors/billingInfo';
import { IBuyPackageData, EBuyPackageStatus } from '../../reducers/types';
import { TActions } from '../../types/actions';

import { getParameters, getPackageStatus } from './utils';

export function* buyPackage() {
  try {
    const action: TActions = (yield take([CONFIRM_BUY_PACKAGE, RESET_BUY_PACKAGE])) as TActions;
    const billingUserType: EAccountType = yield select(selectBillingUserType);
    const data: IBuyPackageData = yield select(selectBuyPackageModalData);

    if (action.type === RESET_BUY_PACKAGE) {
      yield cancel();
    }

    yield put(setBuyPackageLoading({ isLoading: true }));
    const httpApi: IHttpApi = yield getContext('httpApi');
    const logger: ILogger = yield getContext('logger');

    const parameters = getParameters(data);

    const { code, heldPayment, message } = yield call(buyPackageRequest, { httpApi, logger, parameters });
    const status = getPackageStatus(code, billingUserType, heldPayment?.transactionId);

    switch (status) {
      case EBuyPackageStatus.NotEnoughMoneyWithDeposit: {
        // У пользователя-физика недостаточно денег на счете для покупки.
        // Открываем виджет юмани для пополнения
        yield put(setBuyPackageHeldPayment(heldPayment));
        break;
      }

      case EBuyPackageStatus.NotEnoughMoney: {
        // У пользователя-юрлица недостаточно денег на счете для покупки. Ждём, когда он нажмет "Пополнить счет" в попапе,
        // чтобы вызвать ручку оплаты с ходированием покупки
        yield put(setBuyPackageStatus({ status }));

        const action: TActions = (yield take([CONFIRM_BUY_PACKAGE, RESET_BUY_PACKAGE])) as TActions;

        // Прерываем сценарий, если пользователь закрыл модалку
        if (action.type === RESET_BUY_PACKAGE) {
          yield cancel();
        }

        yield put(setBuyPackageLoading({ isLoading: true }));

        // Вызываем ручку оплаты пакета для холдирования
        yield call(buyPackageRequest, {
          httpApi,
          logger,
          parameters: {
            ...parameters,
            needHold: true,
          },
        });

        // Переходим в пополнение кошелька
        window.location.assign('/pay');
        break;
      }

      case EBuyPackageStatus.Ok: {
        yield put(setBuyPackageStatus({ status }));
        yield put(loadPublishTerms());
        break;
      }

      default: {
        yield put(setBuyPackageStatus({ status }));
        yield put(setBuyPackageMessage({ message }));
      }
    }
  } catch (e) {
    yield put(setBuyPackageStatus({ status: EBuyPackageStatus.Error }));
  }
}
